import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getTestimonialsApiUrl } from '../config/globalConfig';

// Safe HTML sanitization function that properly decodes HTML entities, allows basic formatting, and limits to 50 words
const sanitizeHtml = (html, maxWords = 50) => {
    if (!html) return '';
    
    // First, decode HTML entities
    const temp = document.createElement('div');
    temp.innerHTML = html;
    let decoded = temp.textContent || temp.innerText || '';
    
    // Now escape any remaining HTML to prevent XSS
    const escape = document.createElement('div');
    escape.textContent = decoded;
    let escaped = escape.innerHTML;
    
    // Allow only safe HTML tags (bold, italic, line breaks)
    escaped = escaped
        .replace(/&lt;b&gt;/g, '<b>')
        .replace(/&lt;\/b&gt;/g, '</b>')
        .replace(/&lt;i&gt;/g, '<i>')
        .replace(/&lt;\/i&gt;/g, '</i>')
        .replace(/&lt;br\s*\/?&gt;/g, '<br>')
        .replace(/&lt;br&gt;/g, '<br>')
        .replace(/\n/g, '<br>');
    
    // Truncate to max words and add ellipsis if needed
    const words = escaped.split(' ');
    if (words.length > maxWords) {
        const truncated = words.slice(0, maxWords).join(' ');
        return truncated + ' ...';
    }
    
    return escaped;
};

const Testimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [slidesToShow, setSlidesToShow] = useState(2);
    const sliderRef = useRef(null);

    useEffect(() => {
        // set correct slidesToShow on load
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setSlidesToShow(1);
            } else {
                setSlidesToShow(2);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        fetchTestimonials();
    }, []);

    useEffect(() => {
        if (sliderRef.current && testimonials.length > 0) {
            sliderRef.current.slickGoTo(0);
        }
    }, [testimonials]);

    const fetchTestimonials = async () => {
        try {
            const API_URL = getTestimonialsApiUrl('GET_ALL');
            const response = await fetch(API_URL);
            const data = await response.json();
            
            if (data.result && data.Testimonials) {
                // Filter out deleted testimonials and map the data to match the existing structure
                const activeTestimonials = data.Testimonials
                    .filter(testimonial => testimonial.is_deleted === 0)
                    .map(testimonial => ({
                        rating: 5, // Keep 5 stars as default
                        text: testimonial.message,
                        author: testimonial.name,
                        img: testimonial.media_url // Keep the same placeholder image
                    }));
                setTestimonials(activeTestimonials);
            }
        } catch (err) {
            console.error('Error fetching testimonials:', err);
            // Fallback to static testimonials if API fails
            setTestimonials([
                {
                    rating: 5,
                    text: "My teacher a very engaging tutor, explains concepts well, a structured approach to teaching, and cares about her students. She has an amazing voice and is inspiring to learn from.",
                    author: "Janet",
                    img: "/images/testimonial-1.png"
                },
                {
                    rating: 5,
                    text: "HSM has been taking classes for my daughter for past one month, very impressed with the teacher's knowledge and how methodical & engaging classes are. My daughter thoroughly enjoys her classes.",
                    author: "Mitra",
                    img: "/images/testimonial-1.png"
                },
                {
                    rating: 5,
                    text: "My teacher is detailed and precise with her feedback. She is very patient and encouraging. Already after two classes I can hear and feel a HUGE difference in my singing and my voice. :)",
                    author: "Aashika",
                    img: "/images/testimonial-1.png"
                },
                {
                    rating: 5,
                    text: "She's a gem! I've my little one taking singing classes with her. She is soo talented and she knows what she is talking about. Deep knowledge in different vocal training techniques and western classical knowledge. Deeply appreciate the classes. Good going.",
                    author: "Mat",
                    img: "/images/testimonial-1.png"
                },
                {
                    rating: 5,
                    text: "She is very patient, polite and very accommodating with her speech. Grounded and also open to new ideas, nice collaborative talk with her.",
                    author: "Arya",
                    img: "/images/testimonial-1.png"
                }
            ]);
        }
    };
    const settings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 500,
        fade: false,
        cssEase: 'ease-in-out',
        autoplay: true,
        autoplaySpeed: 5000,
        pauseOnHover: false,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        adaptiveHeight: true
    };


    return (
        <section id="testimonials-section">
            <div className="testimonials-container">
                <div className="testimonials-header">
                    <h2 className="section-title">Happy Words From Happy Students</h2>
                </div>

                <div className="testimonials-slider">
                    <Slider key={testimonials.length} ref={sliderRef} {...settings}>
                        {testimonials.map((t, index) => (
                            <div className="testimonial-slide" key={index}>
                                <div className="testimonial-card">
                                    <div className="testimonial-quote">
                                        <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                        </svg>
                                    </div>

                                    <div className="testimonial-rating">
                                        <span className="star filled">★</span>
                                        <span className="star filled">★</span>
                                        <span className="star filled">★</span>
                                        <span className="star filled">★</span>
                                        <span className="star filled">★</span>
                                    </div>

                                    <div className="testimonial-text">
                                        <p dangerouslySetInnerHTML={{ __html: `"${sanitizeHtml(t.text)}"` }}></p>
                                    </div>

                                    <div className="testimonial-author">
                                        {/* <div className="author-image">
                                            <img src={t.img} alt={t.author} />
                                        </div> */}
                                        <div className="author-info">
                                            <span className="author-name">{t.author}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
